import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, User, X, Shield, Mail, Phone, MapPin, UserCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';

const PAGE_INFO = {
  '/':          { title: 'Dashboard',       sub: 'Overview of your business' },
  '/sales':     { title: 'Sales Invoices',  sub: 'Manage customer invoices' },
  '/purchase':  { title: 'Purchase Orders', sub: 'Manage supplier purchases' },
  '/inventory': { title: 'Inventory',       sub: 'Stock levels & ledger' },
  '/customers': { title: 'Customers',       sub: 'Customer master data' },
  '/suppliers': { title: 'Suppliers',       sub: 'Supplier master data' },
  '/items':     { title: 'Items',           sub: 'Product catalogue' },
  '/branches':  { title: 'Branches',        sub: 'Manage branch locations' },
  '/reports':   { title: 'Reports',         sub: 'Analytics & summaries' },
  '/gst':       { title: 'GST Reports',     sub: 'Tax filing & reports' },
  '/users':     { title: 'User Management', sub: 'Roles & access control' },
};

export default function Topbar({ sidebarExpanded }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const menuRef = useRef(null);

  const page = PAGE_INFO[pathname] || { title: 'SIMS', sub: '' };
  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
  });
  const initials = (user?.fullName || 'A').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  // Close menus on outside click
  useEffect(() => {
    function handle(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Fetch full user details when profile modal opens
  useEffect(() => {
    if (profileModal && user?.userID) {
      setLoadingProfile(true);
      usersAPI.getById(user.userID)
        .then(res => {
          setProfileData(res.data?.data || null);
        })
        .catch(err => {
          console.error('Failed to load user profile', err);
        })
        .finally(() => {
          setLoadingProfile(false);
        });
    }
  }, [profileModal, user?.userID]);

  return (
    <>
      <header className={`topbar${sidebarExpanded ? ' sidebar-expanded' : ''}`}>
      {/* Left: Page Info */}
      <div className="tb-left">
        <div>
          <h1 className="tb-title">{page.title}</h1>
          <p className="tb-sub">{today} &nbsp;·&nbsp; {page.sub}</p>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="tb-right">

        {/* User dropdown */}
        <div ref={menuRef} className="tb-user-wrap">
          <button
            className={`tb-user${userMenuOpen ? ' active' : ''}`}
            onClick={() => setUserMenuOpen(o => !o)}
          >
            <div className="tb-avatar">{initials}</div>
            <div className="tb-user-text">
              <span className="tb-user-name">{user?.fullName}</span>
              <span className="tb-user-role">{user?.roleName}</span>
            </div>
            <ChevronDown size={14} className={`tb-chevron${userMenuOpen ? ' up' : ''}`} />
          </button>

          {userMenuOpen && (
            <div className="tb-dropdown">
              <button className="tb-dropdown-item" onClick={() => { setUserMenuOpen(false); setProfileModal(true); }}>
                <User size={14} /> Profile
              </button>
              <hr className="tb-dropdown-divider" />
              <button className="tb-dropdown-item danger" onClick={() => { logout(); setUserMenuOpen(false); }}>
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .topbar {
          position: fixed;
          top: 0;
          left: var(--sidebar-w-mini);
          right: 0;
          height: var(--topbar-h);
          background: #ffffff;
          border-bottom: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px 0 24px;
          z-index: 200;
          gap: 12px;
          transition: left 0.25s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .topbar.sidebar-expanded { left: var(--sidebar-w-full); }

        .tb-left { display: flex; align-items: center; gap: 12px; }
        .tb-title { font-size: 1rem; font-weight: 700; color: var(--text-primary); line-height: 1.1; }
        .tb-sub { font-size: 0.72rem; color: var(--text-muted); margin-top: 1px; }

        .tb-right { display: flex; align-items: center; gap: 6px; }

        /* Icon button */
        .tb-icon-btn {
          position: relative;
          width: 36px; height: 36px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          display: flex; align-items: center; justify-content: center;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        .tb-icon-btn:hover { background: var(--gray-100); color: var(--text-primary); }

        /* Notification dot */
        .tb-notif-dot {
          position: absolute;
          top: 7px; right: 7px;
          width: 7px; height: 7px;
          background: var(--accent-500);
          border-radius: 50%;
          border: 1.5px solid #fff;
        }

        /* Search box */
        .tb-search { position: relative; display: flex; align-items: center; }
        .tb-search.open {
          background: var(--surface);
          border: 1.5px solid var(--primary-400);
          border-radius: var(--radius-sm);
          padding: 0 8px;
          box-shadow: 0 0 0 3px rgba(42,80,128,0.1);
          height: 36px;
          width: 260px;
          transition: width 0.2s ease;
        }
        .tb-search-icon { color: var(--text-muted); flex-shrink: 0; }
        .tb-search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 0.875rem;
          font-family: var(--font);
          color: var(--text-primary);
          background: transparent;
          padding: 0 8px;
        }
        .tb-search-close {
          background: transparent; border: none; cursor: pointer;
          color: var(--text-muted); display: flex; align-items: center;
          padding: 2px;
        }
        .tb-search-close:hover { color: var(--text-primary); }

        /* User button */
        .tb-user-wrap { position: relative; }

        .tb-user {
          display: flex; align-items: center; gap: 8px;
          padding: 5px 10px 5px 6px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        .tb-user:hover, .tb-user.active {
          background: var(--gray-50);
          border-color: var(--gray-300);
        }

        .tb-avatar {
          width: 30px; height: 30px;
          background: linear-gradient(135deg, var(--primary-600), var(--primary-500));
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 700;
          flex-shrink: 0;
          box-shadow: none;
          border: 2px solid rgba(255,255,255,0.9);
          outline: 1.5px solid var(--primary-400);
        }
        .tb-avatar-lg { width: 38px; height: 38px; font-size: 0.875rem; }

        .tb-user-text { display: none; text-align: left; }
        @media (min-width: 640px) { .tb-user-text { display: block; } }

        .tb-user-name { display: block; font-size: 0.8rem; font-weight: 600; color: var(--text-primary); line-height: 1.1; white-space: nowrap; }
        .tb-user-role { display: block; font-size: 0.68rem; color: var(--text-muted); }

        .tb-chevron { color: var(--text-muted); transition: transform 0.2s; }
        .tb-chevron.up { transform: rotate(180deg); }

        /* Dropdown */
        .tb-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          min-width: 220px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-md);
          overflow: hidden;
          animation: modalIn 0.15s ease;
          z-index: 999;
        }

        .tb-dropdown-header {
          display: flex; align-items: center; gap: 10px;
          padding: 14px;
          background: var(--surface-2);
        }
        .tb-dropdown-name { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }
        .tb-dropdown-role { font-size: 0.75rem; color: var(--text-muted); }
        .tb-dropdown-divider { border: none; border-top: 1px solid var(--border); margin: 0; }

        .tb-dropdown-item {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 10px 14px;
          background: transparent; border: none;
          font-size: 0.875rem; font-family: var(--font); font-weight: 500;
          color: var(--text-secondary);
          cursor: pointer; text-align: left;
          transition: background var(--transition-fast), color var(--transition-fast);
        }
        .tb-dropdown-item:hover { background: var(--gray-50); color: var(--text-primary); }
        .tb-dropdown-item.danger:hover { background: var(--danger-light); color: var(--danger-dark); }

        @media (max-width: 768px) {
          .topbar { left: 0 !important; padding: 0 14px 0 52px; }
          .tb-sub { display: none; }
        }
      `}</style>
      </header>

      {profileModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--primary-900) 0%, var(--primary-800) 100%)', color: '#fff', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 'none', borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 24, background: 'rgba(255,255,255,0.15)', border: '2.5px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  {initials}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: '#fff' }}>{profileData?.fullName || user?.fullName || 'User Profile'}</h3>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(212,160,23,0.25)', color: '#ffd666', fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 10, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <Shield size={11} /> {profileData?.roleName || user?.roleName || 'Member'}
                  </span>
                </div>
              </div>
              <button type="button" onClick={() => setProfileModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}>
                <X size={14} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '24px 26px', background: '#f8fafc', overflowY: 'auto', maxHeight: 'calc(92vh - 120px)' }}>
              {loadingProfile && !profileData ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 12 }}>
                  <span className="spinner" style={{ width: 30, height: 30, borderTopColor: 'var(--primary-700)' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading details...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div style={{ background: '#fff', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)', textAlign: 'left' }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Username</label>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', marginTop: 4, fontFamily: 'var(--mono)' }}>@{profileData?.username || user?.username}</div>
                    </div>
                    <div style={{ background: '#fff', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)', textAlign: 'left' }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, background: '#10b981' }} />
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#065f46' }}>Active</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, background: '#fff', padding: '14px 18px', borderRadius: 12, border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Mail size={15} style={{ color: 'var(--primary-500)' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Email Address</div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginTop: 1 }}>{profileData?.email || '—'}</div>
                      </div>
                    </div>
                    <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '2px 0' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Phone size={15} style={{ color: 'var(--primary-500)' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Mobile Number</div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginTop: 1 }}>{profileData?.mobile || '—'}</div>
                      </div>
                    </div>
                    <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '2px 0' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <MapPin size={15} style={{ color: 'var(--primary-500)' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Assigned Branch</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 1 }}>{profileData?.branchName || '—'}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'left' }}>
                    <label style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Authorized Permissions</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(profileData?.permissions || user?.permissions) ? (
                        (profileData?.permissions || user?.permissions).split(',').map(p => p.trim()).filter(Boolean).map(p => (
                          <span key={p} style={{ fontSize: 11, fontWeight: 600, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <UserCheck size={11} /> {p}
                          </span>
                        ))
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No permissions assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', background: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-primary" onClick={() => setProfileModal(false)} style={{ minWidth: 100 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
